import { Theme } from '@mui/material';
import { CommunicationType, DictErrorCode, hasFlag, SECOND, useTostify } from '@sprytki-frontend-apps/common';
import { GateServiceAPI } from 'api/services/gateServiceAPI';
import { initConnection } from 'features/signalr/connection';
import {
	subscribeToAlarmZoneStatusChanged,
	subscribeToLockerGateStatusChanged,
	subscribeToMSpaceLockResult,
} from 'features/signalr/hubs/alertHubMethods';
import { alertStore } from 'stores/alertStore/alertStore';
import { mSpaceStore } from 'stores/mSpaceStore/mSpaceStore';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-v7';
import { AlertFilter, DictLockerStatus, DictOrderStatus, GateType, OpenStatus } from 'shared';
import { appStore } from 'stores/appStore';
import { hubNames } from 'features/signalr/shared/constants';
import { useOnNetworkStatus } from '@sprytki-frontend-apps/common/Hooks';
import {
	IAlarmZoneStatusChangedEvent,
	ILockerGateItem,
	ILockerGateStatusChangedEvent,
	IMSpaceLockEvent,
} from 'api/interfaces/lockerGate';
import { LockerServiceAPI } from 'api/services/lockerServiceAPI';
import { ZoneServiceAPI } from 'api/services/zoneServiceAPI';
import { locationStore } from 'stores/locationStore/locationStore';

const NETWORK_REFETCH_DEBOUNCE_MS = 5 * SECOND;
/**
 * The server starts new wake-up attempts for up to 20s and lets the last one finish (request capped at 20s),
 * so the outcome may arrive up to ~40s after the open was accepted - plus queue and SignalR latency.
 */
const MSPACELOCK_RESULT_TIMEOUT_MS = 45 * SECOND;

export const useAlertsData = () => {
	const { locationId } = useParams();
	const lastNetworkFetchRef = useRef(0);

	const onConnectedCallback = useCallback(() => {
		if (!locationId) return;

		const now = Date.now();
		if (now - lastNetworkFetchRef.current < NETWORK_REFETCH_DEBOUNCE_MS) return;
		lastNetworkFetchRef.current = now;

		alertStore.fetchAlerts(locationId);
	}, [locationId]);

	const fetchAlerts = useCallback(() => {
		if (!locationId) return;

		alertStore.fetchAlerts(locationId);

		const connection = initConnection(hubNames.ALERTS);

		subscribeToLockerGateStatusChanged((event: ILockerGateStatusChangedEvent) => {
			alertStore.updateAlertItem(event);
		});

		subscribeToAlarmZoneStatusChanged((event: IAlarmZoneStatusChangedEvent) => {
			alertStore.updateAlarmZoneState(event);
		});

		subscribeToMSpaceLockResult(
			(event: IMSpaceLockEvent) => mSpaceStore.processOpened(event),
			(event: IMSpaceLockEvent) => mSpaceStore.processNotOpened(event)
		);

		return () => {
			connection.stop();
			alertStore.clearStore();
			mSpaceStore.clear();
		};
	}, [locationId]);

	useOnNetworkStatus(onConnectedCallback);

	useEffect(() => {
		return fetchAlerts();
	}, [locationId]);

	useEffect(() => {
		const interval = setInterval(alertStore.updateItemsOpenStatus, 60 * SECOND);

		return () => clearInterval(interval);
	}, []);

	return { fetchedAlerts: alertStore.alerts, isLoading: alertStore.isLoading, fetchAlerts };
};

export const filterDataByAlertFilter = (data: ILockerGateItem, filter: AlertFilter): boolean => {
	switch (filter) {
		case AlertFilter.All:
			return true;
		case AlertFilter.Opened:
			return data.openStatus !== OpenStatus.Closed;
		case AlertFilter.Rented:
			return (
				data.isLocker &&
				(data.lockerStatus === DictLockerStatus.Rented || data.lockerStatus === DictLockerStatus.Resigned)
			);
		case AlertFilter.PendingVerification:
			return data.isLocker && data.lockerStatus === DictLockerStatus.PendingVerification;
		case AlertFilter.Available:
			return data.isLocker && data.lockerStatus === DictLockerStatus.Available;
		case AlertFilter.OutOfUse:
			return data.isLocker && data.lockerStatus === DictLockerStatus.OutOfUse;
		case AlertFilter.Gates:
			return !data.isLocker;
		case AlertFilter.Unpaid:
			return (
				data.isLocker &&
				!!data.lockerDetails?.tenantDetails &&
				data.lockerDetails?.rentalDetails?.isPaid === false
			);
		case AlertFilter.Padlock:
			return data.isLocker && data.padLockDetails?.padlockActionNeeded === true;
		default:
			return true;
	}
};

export const filterDataByActiveFilters = (data: ILockerGateItem, activeFilters: Set<AlertFilter>): boolean => {
	if (activeFilters.size === 0) return false;
	for (const filter of activeFilters) {
		if (filterDataByAlertFilter(data, filter)) return true;
	}
	return false;
};

const normalize = (str?: string) => str?.replace(/\s+/g, ' ').trim().toLowerCase();

export const filterDataBySearch = (data: ILockerGateItem, searchPhrase: string) => {
	const normalizedSearch = normalize(searchPhrase);
	return (
		!normalizedSearch ||
		normalize(data.userFriendlyId)?.includes(normalizedSearch) ||
		normalize(data.tenantName)?.includes(normalizedSearch) ||
		normalize(data.userFriendlyId)?.includes(normalizedSearch) ||
		normalize(data.lastUserName)?.includes(normalizedSearch)
	);
};

export const filterData = (data: ILockerGateItem, searchPhrase: string, activeFilters: Set<AlertFilter>) =>
	filterDataByActiveFilters(data, activeFilters) && filterDataBySearch(data, searchPhrase);

export const getIconColor = (openStatus: OpenStatus, theme: Theme) => {
	switch (openStatus) {
		case OpenStatus.Closed:
			return theme.palette.inactive.main;
		case OpenStatus.Open:
			return theme.palette.secondary.main;
		case OpenStatus.OpenTooLong:
			return theme.palette.error.light;
		default:
			return theme.palette.inactive.main;
	}
};

// Match AP (getRentDetailsStatusColors + BasicData): active rentals green, reservations and
// not-yet-started rentals orange.
const AP_RENT_ORANGE = '#F49507';

export const getRentStatusColor = (
	rentalDetails: { orderStatus: DictOrderStatus; rentedFrom?: string | null },
	theme: Theme
): string | undefined => {
	if (rentalDetails.rentedFrom && new Date(rentalDetails.rentedFrom) > new Date()) return AP_RENT_ORANGE;

	switch (rentalDetails.orderStatus) {
		case DictOrderStatus.Created:
		case DictOrderStatus.Pending:
		case DictOrderStatus.Successful:
			return theme.palette.green.main;
		case DictOrderStatus.Reservation:
		case DictOrderStatus.Potential:
			return AP_RENT_ORANGE;
		default:
			return undefined;
	}
};

export const getAlarmIconColor = (armed: boolean, theme: Theme) => {
	if (armed) {
		return theme.palette.inactive.light;
	} else {
		return theme.palette.error.light;
	}
};

export const useOpenItem = (isLocker: boolean, gateType: GateType, id: string, isMonitored = true) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const startLoading = () => appStore.setIsLoading(true);
	const stopLoading = () => appStore.setIsLoading(false);

	const openItem = async () => {
		try {
			startLoading();
			const endpoint = isLocker ? LockerServiceAPI : GateServiceAPI;
			// When the open button was clicked — lets us ignore a "closed" reported from before the click.
			const clickedAt = Date.now();
			const response = await endpoint.open(id);

			const successMessage = () => {
				if (gateType === GateType.Alarm) return t('pages.alerts.successAlarmMessage');

				return t(isLocker ? 'pages.alerts.successLockerMessage' : 'pages.alerts.successGateMessage');
			};

			const failMessage = () => {
				if (gateType === GateType.Alarm) return t('pages.alerts.failAlarmMessage');

				return t(isLocker ? 'pages.alerts.failLockerMessage' : 'pages.alerts.failGateMessage');
			};

			const handleEvent = (event: ILockerGateStatusChangedEvent): boolean => {
				const isOpen = event.openStatus === OpenStatus.ReadyToOpen || event.openStatus === OpenStatus.Open;

				// A "closed" reported from before the click is a stale snapshot (the backend re-broadcasts the old
				// status when the command is sent) — not the result of this open. Ignore it and keep waiting.
				const closedBeforeClick = !!event.lastClosedAtUTC && Date.parse(event.lastClosedAtUTC) < clickedAt;
				if (!isOpen && closedBeforeClick) {
					return false;
				}

				if (isOpen) {
					toast(successMessage(), 'success');
				} else {
					toast(failMessage());
				}
				stopLoading();
				return true;
			};

			const onTimeoutCallback = () => {
				toast(failMessage());
				stopLoading();
			};

			if (response.success) {
				// An unmonitored box never reports "opened", so waiting for the event would always time out
				// into a false error. Like a gate, the accepted command is the outcome.
				if (isLocker && isMonitored) {
					alertStore.onUpdateAlertItem(id, handleEvent, onTimeoutCallback);
				} else {
					toast(successMessage(), 'success');
					stopLoading();
				}
			} else {
				toast(failMessage());
				stopLoading();
			}

			return response.success;
		} catch (error: any) {
			const code = error?.response?.data?.errors[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(t(`errorCodes.${code}`, { defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`) }));
			stopLoading();
		}
	};
	return { openItem };
};

export const isTTLockLocation = () =>
	hasFlag(locationStore.selectedLocation?.accessCommunicationType ?? 0, CommunicationType.TTLock as number);

// TTLock items open synchronously and have no open/close monitoring, so the HTTP response
// already carries the outcome — there is no LockerGateStatusChanged event to wait for.
export const useOpenTTLock = (isLocker: boolean, id: string) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const openTTLock = async () => {
		try {
			appStore.setIsLoading(true);
			const endpoint = isLocker ? LockerServiceAPI : GateServiceAPI;
			const response = await endpoint.open(id);

			if (response.success) {
				toast(t(isLocker ? 'pages.alerts.successLockerMessage' : 'pages.alerts.successGateMessage'), 'success');
			} else {
				toast(t(isLocker ? 'pages.alerts.failLockerMessage' : 'pages.alerts.failGateMessage'));
			}

			return response.success;
		} catch (error: any) {
			const code = error?.response?.data?.errors[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(t(`errorCodes.${code}`, { defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`) }));
		} finally {
			appStore.setIsLoading(false);
		}
	};

	return { openTTLock };
};

export const useOpenMSpaceLock = (id: string, isLocker: boolean, onClose: () => void) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const successMessage = isLocker ? 'pages.alerts.successLockerMessage' : 'pages.alerts.successGateMessage';
	const failMessage = isLocker ? 'pages.alerts.failLockerMessage' : 'pages.alerts.failGateMessage';

	const onOpened = () => {
		toast(t(successMessage), 'success');
		onClose();
	};

	const onNotOpened = () => {
		toast(t(failMessage));
		onClose();
	};

	const mSpaceTimeoutCallback = () => {
		// gates never sleep, so the "wake the lock" hint must not be shown for them
		toast(t(isLocker ? 'pages.alerts.openLockerTimeoutMSpaceLock' : 'pages.alerts.openGateTimeoutMSpaceLock'));
		onClose();
	};

	const openMSpaceLock = async () => {
		mSpaceStore.onResult(id, onOpened, onNotOpened, mSpaceTimeoutCallback, MSPACELOCK_RESULT_TIMEOUT_MS);

		try {
			const endpoint = isLocker ? LockerServiceAPI : GateServiceAPI;
			const response = await endpoint.open(id);

			if (!response.success) {
				mSpaceStore.clear();
				toast(t(failMessage));
				onClose();
			}
		} catch (error: any) {
			mSpaceStore.clear();
			const code = error?.response?.data?.errors[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(t(`errorCodes.${code}`, { defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`) }));
			onClose();
		}
	};

	return { openMSpaceLock };
};

export const useDisarmLocker = (id: string, newAlarmState: boolean) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const changeAlarmState = async () => {
		try {
			appStore.setIsLoading(true);
			const response = newAlarmState ? await LockerServiceAPI.arm(id) : await LockerServiceAPI.disarm(id);

			const successMessage = () => {
				return t(newAlarmState ? 'pages.alerts.successArmAlarmMessage' : 'pages.alerts.successAlarmMessage');
			};

			const failMessage = () => {
				return t(newAlarmState ? 'pages.alerts.failArmAlarmMessage' : 'pages.alerts.failAlarmMessage');
			};

			if (response.success) {
				toast(successMessage(), 'success');
			} else {
				toast(failMessage());
			}
			return response.success;
		} catch (error: any) {
			const code = error?.response?.data?.errors[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(t(`errorCodes.${code}`, { defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`) }));
		} finally {
			appStore.setIsLoading(false);
		}
	};
	return { changeAlarmState };
};

export const useChangeZoneAlarmState = (id: string, newAlarmState: boolean) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const changeAlarmState = async () => {
		try {
			appStore.setIsLoading(true);
			const response = newAlarmState ? await ZoneServiceAPI.arm(id) : await ZoneServiceAPI.disarm(id);

			const successMessage = () => {
				return t(
					newAlarmState ? 'pages.alerts.successArmZoneMessage' : 'pages.alerts.successDisarmZoneMessage'
				);
			};

			const failMessage = () => {
				return t(newAlarmState ? 'pages.alerts.failArmZoneMessage' : 'pages.alerts.failDisarmZoneMessage');
			};

			if (response.success) {
				toast(successMessage(), 'success');
			} else {
				toast(failMessage());
			}
			return response.success;
		} catch (error: any) {
			const code = error?.response?.data?.errors[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(t(`errorCodes.${code}`, { defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`) }));
		} finally {
			appStore.setIsLoading(false);
		}
	};
	return { changeAlarmState };
};

export const usePadLock = (lockerId: string, padLockInPlace: boolean) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const handlePadLock = async (code?: string) => {
		if (appStore.isLoading) return false;

		const successKey = padLockInPlace ? 'pages.lockers.padLock.unlockSuccess' : 'pages.lockers.padLock.lockSuccess';
		const errorMessage = t('pages.lockers.padLock.error');

		const currentItem = alertStore.lockerGateItems.get(lockerId);
		const previousPadLockCode = currentItem?.padLockDetails?.padLockCode;

		appStore.setIsLoading(true);
		try {
			const payload = { code: code ?? '' };
			const response = padLockInPlace
				? await LockerServiceAPI.removePadLock(lockerId)
				: await LockerServiceAPI.insertPadLock(lockerId, payload);

			if (!response.success) {
				toast(errorMessage, 'error');
				return false;
			}

			const updatedPadLockDetails = padLockInPlace
				? { padLockInPlace: false, padLockCode: previousPadLockCode ?? null }
				: {
						padLockInPlace: true,
						padLockCode: code && code.length > 0 ? code : null,
					};
			alertStore.updatePadLockDetails(lockerId, updatedPadLockDetails);

			toast(t(successKey), 'success');
			return true;
		} catch (error: any) {
			const errorCode = error?.response?.data?.errors?.[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(
				t(`errorCodes.${errorCode}`, {
					defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`),
				})
			);
			return false;
		} finally {
			appStore.setIsLoading(false);
		}
	};

	return { handlePadLock };
};

export const useLockerOutOfUse = (lockerId: string, isOutOfUse: boolean) => {
	const { t } = useTranslation();
	const toast = useTostify();

	const handleOutOfUse = async (reason?: string) => {
		if (appStore.isLoading) return false;

		const successKey = isOutOfUse ? 'dialogs.outOfUse.enableSuccess' : 'dialogs.outOfUse.disableSuccess';
		const errorMessage = t('dialogs.outOfUse.error');

		appStore.setIsLoading(true);
		try {
			const payload = isOutOfUse
				? { isOutOfUse: false, outOfUseReason: null }
				: { isOutOfUse: true, outOfUseReason: reason ?? null };

			const response = await LockerServiceAPI.setOutOfUse(lockerId, payload);

			if (!response.success) {
				toast(errorMessage, 'error');
				return false;
			}

			alertStore.updateLockerStatus(
				lockerId,
				payload.isOutOfUse ? DictLockerStatus.OutOfUse : DictLockerStatus.Available
			);

			alertStore.updateOutOfUseReason(lockerId, payload.outOfUseReason);

			toast(t(successKey), 'success');
			return true;
		} catch (error: any) {
			const errorCode = error?.response?.data?.errors?.[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(
				t(`errorCodes.${errorCode}`, {
					defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`),
				})
			);
			return false;
		} finally {
			appStore.setIsLoading(false);
		}
	};

	return { handleOutOfUse };
};

const SCROLL_RETRY_DELAY = 120;
const MAX_SCROLL_ATTEMPTS = 6;
const SCROLL_OFFSET = 96;

export const useScrollToHash = (hasData: boolean): void => {
	const { hash } = useLocation();
	useEffect(() => {
		if (!hash || !hasData) return;

		const elementId = hash.replace('#', '');
		const timeouts = new Set<number>();
		let cancelled = false;

		const clearAllTimeouts = () => {
			timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
			timeouts.clear();
		};

		const schedule = (callback: () => void, delay = 0) => {
			const timeoutId = window.setTimeout(() => {
				timeouts.delete(timeoutId);
				if (!cancelled) callback();
			}, delay);
			timeouts.add(timeoutId);
		};

		const attemptScroll = (attempt = 0) => {
			if (cancelled || attempt > MAX_SCROLL_ATTEMPTS) return;

			const element = document.getElementById(elementId);
			if (!element) {
				schedule(() => attemptScroll(attempt + 1), SCROLL_RETRY_DELAY);
				return;
			}

			const targetTop = Math.max(element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET, 0);
			const behavior: ScrollBehavior = attempt === 0 ? 'smooth' : 'auto';
			window.scrollTo({ top: targetTop, behavior });

			schedule(() => {
				const currentTop = element.getBoundingClientRect().top;
				if (Math.abs(currentTop - SCROLL_OFFSET) > 2) {
					attemptScroll(attempt + 1);
				}
			}, SCROLL_RETRY_DELAY);
		};

		schedule(() => attemptScroll());

		return () => {
			cancelled = true;
			clearAllTimeouts();
		};
	}, [hash, hasData]);
};

export const getItemTypeLabel = (
	item: Pick<ILockerGateItem, 'isLocker' | 'gateType' | 'alarmZoneMode'> & { orderItemTypeTag?: string | null },
	t: (key: string, options?: { defaultValue?: string }) => string
): string => {
	if (item.alarmZoneMode != null) return t('common.zone');
	if (item.gateType === GateType.Alarm) return t('common.alarm');
	if (!item.isLocker) return t('common.gate');

	return (
		(item.orderItemTypeTag && t(`common.lockerType_${item.orderItemTypeTag}`, { defaultValue: '' })) ||
		t('common.locker')
	);
};
